import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import useSmartDocument from "../hooks/useSmartDocument";
import useKnowledgeDB from "../hooks/useKnowledgeDB";
import CreateDocumentModal from "../components/SmartDocument/CreateDocumentModal";
import { showConfirm } from "../modal/confirmModal";
import {
  PlusCircle,
  FileText,
  Clock,
  Database,
  Search,
  Trash2,
  Edit3,
  ChevronRight,
  Sparkles,
  Filter,
  Grid,
  List,
  Brain,
  Zap,
  Star,
  TrendingUp,
  BookOpen,
  Layers,
  ArrowRight,
  Info,
  Calendar,
  FolderOpen,
  MoreVertical,
  Eye,
  Copy,
  Archive,
  SortDesc,
  Activity,
  BarChart3,
  RefreshCw,
  Download,
  Share2,
} from "lucide-react";

const SmartDocumentListPage = () => {
  const navigate = useNavigate();
  const { listSessions, deleteDocument, formatDate, createDocument } =
    useSmartDocument();
  const { getDatabases } = useKnowledgeDB();

  const [sessions, setSessions] = useState([]);
  const [databases, setDatabases] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [viewMode, setViewMode] = useState("grid");
  const [sortBy, setSortBy] = useState("updated");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [hoveredCard, setHoveredCard] = useState(null);
  const [selectedFilter, setSelectedFilter] = useState("all");
  const [creatingDocument, setCreatingDocument] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    const [sessionsResult, databasesResult] = await Promise.all([
      listSessions(),
      getDatabases(),
    ]);

    if (sessionsResult.ok) {
      setSessions(sessionsResult.data);
    }
    if (databasesResult.ok) {
      setDatabases(
        databasesResult.databases.filter(
          (db) => db.database_status === "complete"
        )
      );
    }
    setLoading(false);
  };

  const handleOpenDocument = (sessionId) => {
    navigate(`/smart_editor_section/${sessionId}`);
  };

  const handleDeleteDocument = async (e, sessionId) => {
    e.stopPropagation();

    const confirmed = await showConfirm({
      title: "문서 삭제",
      message:
        "이 문서를 삭제하시겠습니까? 모든 버전과 편집 이력이 삭제됩니다.",
      confirmText: "삭제",
      cancelText: "취소",
      variant: "error",
    });

    if (!confirmed) return;

    const result = await deleteDocument(sessionId);
    if (result.ok) {
      await loadData();
    }
  };

  const handleCreateDocument = async (data) => {
    setCreatingDocument(true);
    setShowCreateModal(false);

    try {
      // 직접 문서 생성 API 호출
      const result = await createDocument(data);

      if (result.ok) {
        // 생성 성공 시 상세 페이지로 이동
        navigate(`/smart_editor_section/${result.sessionId}`);
      } else {
        // 에러 처리
        console.error("문서 생성 실패:", result.error);
        alert(`문서 생성 실패: ${result.error}`);
        setCreatingDocument(false);
      }
    } catch (error) {
      console.error("문서 생성 오류:", error);
      alert("문서 생성 중 오류가 발생했습니다.");
      setCreatingDocument(false);
    }
  };

  const filteredSessions = sessions
    .filter((session) =>
      session.template_name?.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .filter((session) => {
      if (selectedFilter === "all") return true;
      if (selectedFilter === "with-db") return session.database_count > 0;
      if (selectedFilter === "no-db") return session.database_count === 0;
      return true;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case "name":
          return a.template_name.localeCompare(b.template_name);
        case "created":
          return new Date(b.created_at) - new Date(a.created_at);
        case "updated":
        default:
          return new Date(b.updated_at) - new Date(a.updated_at);
      }
    });

  const getTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = Math.floor((now - date) / 1000);

    if (diff < 60) return "방금 전";
    if (diff < 3600) return `${Math.floor(diff / 60)}분 전`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}시간 전`;
    if (diff < 604800) return `${Math.floor(diff / 86400)}일 전`;
    return formatDate(dateString);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      {/* Animated Background Elements */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-purple-300 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-yellow-300 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-2000"></div>
        <div className="absolute top-40 left-1/2 w-80 h-80 bg-pink-300 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-4000"></div>
      </div>

      {/* Header */}
      <div className="relative backdrop-blur-lg bg-white/70 border-b border-white/20 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-8">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="relative group">
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl blur-lg group-hover:blur-xl transition-all opacity-60"></div>
                  <div className="relative p-3 bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl">
                    <Sparkles className="w-10 h-10 text-white animate-pulse" />
                  </div>
                </div>
                <div>
                  <h1 className="text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
                    AI 스마트 문서
                  </h1>
                  <p className="text-sm text-gray-600 mt-1 flex items-center gap-2">
                    <Brain className="w-4 h-4" />
                    지식 DB와 연결된 지능형 문서 작성 시스템
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowCreateModal(true)}
                className="group relative px-8 py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-2xl hover:shadow-2xl transition-all transform hover:scale-105"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl blur opacity-40 group-hover:opacity-60 transition-opacity"></div>
                <div className="relative flex items-center gap-2">
                  <PlusCircle className="w-5 h-5" />
                  <span className="font-semibold">새 문서 만들기</span>
                  <Zap className="w-4 h-4 animate-bounce" />
                </div>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Knowledge DB Info Banner */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-6">
        <div className="relative overflow-hidden bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 rounded-2xl p-6 text-white">
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl"></div>
          <div className="relative flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Database className="w-12 h-12" />
              <div>
                <h3 className="text-lg font-bold flex items-center gap-2">
                  지식 데이터베이스 (Knowledge DB)란?
                  <Info className="w-4 h-4" />
                </h3>
                <p className="text-white/90 text-sm mt-1">
                  AI가 참고할 수 있는 기업 고유의 데이터 저장소입니다. 문서 작성
                  시 연결된 DB의 정보를 활용해 더 정확하고 맞춤화된 콘텐츠를
                  생성합니다.
                </p>
              </div>
            </div>
            <div className="flex gap-3">
              <div className="bg-white/20 backdrop-blur-sm rounded-xl px-4 py-2">
                <div className="text-2xl font-bold">{databases.length}</div>
                <div className="text-xs">활성 DB</div>
              </div>
              <div className="bg-white/20 backdrop-blur-sm rounded-xl px-4 py-2">
                <div className="text-2xl font-bold">
                  {sessions.filter((s) => s.database_count > 0).length}
                </div>
                <div className="text-xs">연결된 문서</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex items-center justify-between gap-4">
          <div className="flex-1 max-w-md">
            <div className="relative group">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 group-focus-within:text-blue-600 transition-colors" />
              <input
                type="text"
                placeholder="문서 검색..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 bg-white/80 backdrop-blur-sm border border-gray-200 rounded-2xl focus:outline-none focus:ring-4 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
              />
            </div>
          </div>

          <div className="flex items-center gap-3">
            {/* Filter Buttons */}
            <div className="flex bg-white/80 backdrop-blur-sm rounded-2xl p-1 border border-gray-200">
              <button
                onClick={() => setSelectedFilter("all")}
                className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                  selectedFilter === "all"
                    ? "bg-gradient-to-r from-blue-500 to-purple-500 text-white shadow-lg"
                    : "text-gray-600 hover:text-gray-900"
                }`}
              >
                전체
              </button>
              <button
                onClick={() => setSelectedFilter("with-db")}
                className={`px-4 py-2 rounded-xl text-sm font-medium transition-all flex items-center gap-1 ${
                  selectedFilter === "with-db"
                    ? "bg-gradient-to-r from-blue-500 to-purple-500 text-white shadow-lg"
                    : "text-gray-600 hover:text-gray-900"
                }`}
              >
                <Database className="w-3 h-3" />
                DB 연결
              </button>
              <button
                onClick={() => setSelectedFilter("no-db")}
                className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                  selectedFilter === "no-db"
                    ? "bg-gradient-to-r from-blue-500 to-purple-500 text-white shadow-lg"
                    : "text-gray-600 hover:text-gray-900"
                }`}
              >
                독립 문서
              </button>
            </div>

            {/* Sort Dropdown */}
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-4 py-2.5 bg-white/80 backdrop-blur-sm border border-gray-200 rounded-2xl focus:outline-none focus:ring-4 focus:ring-blue-500/20 focus:border-blue-500 text-sm font-medium"
            >
              <option value="updated">최근 수정순</option>
              <option value="created">생성일순</option>
              <option value="name">이름순</option>
            </select>

            {/* View Mode Toggle */}
            <div className="flex bg-white/80 backdrop-blur-sm rounded-2xl p-1 border border-gray-200">
              <button
                onClick={() => setViewMode("grid")}
                className={`p-2.5 rounded-xl transition-all ${
                  viewMode === "grid"
                    ? "bg-gradient-to-r from-blue-500 to-purple-500 text-white shadow-lg"
                    : "text-gray-500 hover:text-gray-900"
                }`}
              >
                <Grid className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode("list")}
                className={`p-2.5 rounded-xl transition-all ${
                  viewMode === "list"
                    ? "bg-gradient-to-r from-blue-500 to-purple-500 text-white shadow-lg"
                    : "text-gray-500 hover:text-gray-900"
                }`}
              >
                <List className="w-4 h-4" />
              </button>
            </div>

            {/* Refresh Button */}
            <button
              onClick={loadData}
              className="p-2.5 bg-white/80 backdrop-blur-sm border border-gray-200 rounded-2xl hover:bg-gray-50 transition-all"
            >
              <RefreshCw
                className={`w-4 h-4 ${loading ? "animate-spin" : ""}`}
              />
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="mt-6 grid grid-cols-4 gap-4">
          <div className="group relative overflow-hidden bg-white/80 backdrop-blur-sm rounded-2xl p-5 border border-gray-200 hover:shadow-xl transition-all">
            <div className="absolute top-0 right-0 w-20 h-20 bg-blue-500/10 rounded-full blur-2xl group-hover:bg-blue-500/20 transition-all"></div>
            <div className="relative flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide">
                  전체 문서
                </p>
                <p className="text-3xl font-bold text-gray-900 mt-1">
                  {sessions.length}
                </p>
                <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                  <TrendingUp className="w-3 h-3" />
                  12% 증가
                </p>
              </div>
              <div className="p-3 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl">
                <FileText className="w-6 h-6 text-white" />
              </div>
            </div>
          </div>

          <div className="group relative overflow-hidden bg-white/80 backdrop-blur-sm rounded-2xl p-5 border border-gray-200 hover:shadow-xl transition-all">
            <div className="absolute top-0 right-0 w-20 h-20 bg-purple-500/10 rounded-full blur-2xl group-hover:bg-purple-500/20 transition-all"></div>
            <div className="relative flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide">
                  지식 DB
                </p>
                <p className="text-3xl font-bold text-gray-900 mt-1">
                  {databases.length}
                </p>
                <p className="text-xs text-purple-600 mt-1">활성 상태</p>
              </div>
              <div className="p-3 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl">
                <Database className="w-6 h-6 text-white" />
              </div>
            </div>
          </div>

          <div className="group relative overflow-hidden bg-white/80 backdrop-blur-sm rounded-2xl p-5 border border-gray-200 hover:shadow-xl transition-all">
            <div className="absolute top-0 right-0 w-20 h-20 bg-green-500/10 rounded-full blur-2xl group-hover:bg-green-500/20 transition-all"></div>
            <div className="relative flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide">
                  총 버전
                </p>
                <p className="text-3xl font-bold text-gray-900 mt-1">
                  {sessions.reduce(
                    (acc, s) => acc + (s.current_version || 0),
                    0
                  )}
                </p>
                <p className="text-xs text-gray-500 mt-1">편집 이력</p>
              </div>
              <div className="p-3 bg-gradient-to-br from-green-500 to-green-600 rounded-xl">
                <Layers className="w-6 h-6 text-white" />
              </div>
            </div>
          </div>

          <div className="group relative overflow-hidden bg-white/80 backdrop-blur-sm rounded-2xl p-5 border border-gray-200 hover:shadow-xl transition-all">
            <div className="absolute top-0 right-0 w-20 h-20 bg-orange-500/10 rounded-full blur-2xl group-hover:bg-orange-500/20 transition-all"></div>
            <div className="relative flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide">
                  AI 활용도
                </p>
                <p className="text-3xl font-bold text-gray-900 mt-1">89%</p>
                <p className="text-xs text-orange-600 mt-1 flex items-center gap-1">
                  <Activity className="w-3 h-3" />
                  높음
                </p>
              </div>
              <div className="p-3 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl">
                <Brain className="w-6 h-6 text-white" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
        {/* 문서 생성 중 로딩 오버레이 */}
        {creatingDocument && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
            <div className="bg-white rounded-3xl p-8 shadow-2xl">
              <div className="flex flex-col items-center gap-6">
                <div className="relative">
                  <div className="w-24 h-24 border-4 border-blue-200 rounded-full animate-pulse"></div>
                  <div className="absolute top-0 left-0 w-24 h-24 border-4 border-blue-600 rounded-full animate-spin border-t-transparent"></div>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <Sparkles className="w-10 h-10 text-blue-600 animate-pulse" />
                  </div>
                </div>
                <div className="text-center">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    AI 문서를 생성하는 중...
                  </h3>
                  <p className="text-sm text-gray-600 max-w-sm">
                    선택한 지식 DB를 기반으로 스마트 문서를 준비하고 있습니다
                  </p>
                  <div className="mt-4 flex items-center justify-center gap-2">
                    <Brain className="w-5 h-5 text-purple-600 animate-bounce" />
                    <Database className="w-5 h-5 text-blue-600 animate-bounce animation-delay-200" />
                    <FileText className="w-5 h-5 text-green-600 animate-bounce animation-delay-400" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <div className="relative">
                <div className="w-20 h-20 border-4 border-blue-200 rounded-full animate-pulse"></div>
                <div className="absolute top-0 left-0 w-20 h-20 border-4 border-blue-600 rounded-full animate-spin border-t-transparent"></div>
              </div>
              <p className="mt-6 text-gray-600 font-medium">
                문서를 불러오는 중...
              </p>
            </div>
          </div>
        ) : filteredSessions.length === 0 ? (
          <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-16 text-center border border-gray-200">
            <div className="max-w-md mx-auto">
              <div className="w-24 h-24 bg-gradient-to-br from-gray-100 to-gray-200 rounded-full flex items-center justify-center mx-auto mb-6">
                <FileText className="w-12 h-12 text-gray-400" />
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-3">
                {searchTerm ? "검색 결과가 없습니다" : "아직 문서가 없습니다"}
              </h3>
              <p className="text-gray-500 mb-8">
                {searchTerm
                  ? "다른 검색어를 시도해보세요"
                  : "AI와 함께 첫 번째 스마트 문서를 만들어보세요"}
              </p>
              {!searchTerm && (
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="group relative px-8 py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-2xl hover:shadow-2xl transition-all transform hover:scale-105 mx-auto inline-flex items-center gap-3"
                >
                  <PlusCircle className="w-5 h-5" />
                  <span className="font-semibold">첫 문서 만들기</span>
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </button>
              )}
            </div>
          </div>
        ) : viewMode === "grid" ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredSessions.map((session) => (
              <div
                key={session.id}
                onClick={() => handleOpenDocument(session.id)}
                onMouseEnter={() => setHoveredCard(session.id)}
                onMouseLeave={() => setHoveredCard(null)}
                className="group relative bg-white/80 backdrop-blur-sm rounded-2xl border border-gray-200 hover:border-blue-400 hover:shadow-2xl transition-all cursor-pointer transform hover:-translate-y-1"
              >
                {/* Card Glow Effect */}
                {hoveredCard === session.id && (
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-400/20 to-purple-400/20 rounded-2xl blur-xl"></div>
                )}

                <div className="relative p-6">
                  {/* Header */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h3 className="text-lg font-bold text-gray-900 group-hover:text-blue-600 transition-colors line-clamp-2">
                        {session.template_name}
                      </h3>
                      <div className="flex items-center gap-2 mt-3">
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-blue-100 text-blue-700 rounded-lg text-xs font-medium">
                          <Layers className="w-3 h-3" />v
                          {session.current_version}
                        </span>
                        {session.database_count > 0 && (
                          <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-purple-100 text-purple-700 rounded-lg text-xs font-medium">
                            <Database className="w-3 h-3" />
                            {session.database_count} DB 연결
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="p-2 bg-gray-50 rounded-lg group-hover:bg-blue-50 transition-colors">
                      <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-blue-600 transition-colors" />
                    </div>
                  </div>

                  {/* AI Insight */}
                  <div className="p-3 bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl mb-4">
                    <div className="flex items-center gap-2 text-xs text-gray-600">
                      <Brain className="w-4 h-4 text-purple-500" />
                      <span>
                        AI가{" "}
                        {session.database_count > 0
                          ? `${session.database_count}개의 지식 DB를 참조하여`
                          : "기본 모델로"}{" "}
                        작성 지원
                      </span>
                    </div>
                  </div>

                  {/* Footer */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1 text-sm text-gray-500">
                      <Clock className="w-4 h-4" />
                      <span>{getTimeAgo(session.updated_at)}</span>
                    </div>
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          // 복사 기능
                        }}
                        className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                      <button
                        onClick={(e) => handleDeleteDocument(e, session.id)}
                        className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Hover Indicator */}
                <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-500 to-purple-500 rounded-b-2xl transform scale-x-0 group-hover:scale-x-100 transition-transform"></div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl overflow-hidden border border-gray-200">
            <table className="w-full">
              <thead className="bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    문서명
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    버전
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    지식 DB
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    AI 지원
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    최근 수정
                  </th>
                  <th className="px-6 py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    작업
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredSessions.map((session) => (
                  <tr
                    key={session.id}
                    onClick={() => handleOpenDocument(session.id)}
                    className="hover:bg-blue-50/50 cursor-pointer transition-colors"
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-gradient-to-br from-blue-100 to-purple-100 rounded-lg">
                          <FileText className="w-4 h-4 text-blue-600" />
                        </div>
                        <div className="font-medium text-gray-900 hover:text-blue-600 transition-colors">
                          {session.template_name}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-blue-100 text-blue-700 rounded-lg text-xs font-medium">
                        <Layers className="w-3 h-3" />v{session.current_version}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      {session.database_count > 0 ? (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-purple-100 text-purple-700 rounded-lg text-xs font-medium">
                          <Database className="w-3 h-3" />
                          {session.database_count} 개 연결
                        </span>
                      ) : (
                        <span className="text-gray-400 text-sm">미연결</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <Brain className="w-4 h-4 text-purple-500" />
                        <span className="text-sm text-gray-600">
                          {session.database_count > 0 ? "고급 AI" : "기본 AI"}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {getTimeAgo(session.updated_at)}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                          }}
                          className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={(e) => handleDeleteDocument(e, session.id)}
                          className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <CreateDocumentModal
          databases={databases}
          onClose={() => setShowCreateModal(false)}
          onSubmit={handleCreateDocument}
        />
      )}

      <style jsx>{`
        @keyframes blob {
          0% {
            transform: translate(0px, 0px) scale(1);
          }
          33% {
            transform: translate(30px, -50px) scale(1.1);
          }
          66% {
            transform: translate(-20px, 20px) scale(0.9);
          }
          100% {
            transform: translate(0px, 0px) scale(1);
          }
        }
        .animate-blob {
          animation: blob 7s infinite;
        }
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        .animation-delay-4000 {
          animation-delay: 4s;
        }
      `}</style>
    </div>
  );
};

export default SmartDocumentListPage;
